import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DownloadIcon from '@mui/icons-material/Download'
import { driverService, vehicleService } from '../api/adminServices'
import { PropertySection } from './PropertyView'

/** File kinds recognised from the leading bytes of the base64 payload (the backend
 *  returns raw bytes without a content type). */
const SIGNATURES: { prefix: string; mime: string; ext: string }[] = [
  { prefix: '/9j/', mime: 'image/jpeg', ext: 'jpg' },
  { prefix: 'iVBORw0KGgo', mime: 'image/png', ext: 'png' },
  { prefix: 'R0lGOD', mime: 'image/gif', ext: 'gif' },
  { prefix: 'UklGR', mime: 'image/webp', ext: 'webp' },
  { prefix: 'JVBERi0', mime: 'application/pdf', ext: 'pdf' },
]

function detectType(base64: string) {
  return (
    SIGNATURES.find((s) => base64.startsWith(s.prefix)) ?? {
      mime: 'application/octet-stream',
      ext: 'bin',
    }
  )
}

/** Turns base64 content into an object URL (works for "open in new tab" with PDFs,
 *  unlike data: URLs) and revokes it when the content changes or the tile unmounts. */
function useBlobUrl(base64: string | null | undefined) {
  const type = useMemo(() => (base64 ? detectType(base64) : null), [base64])
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!base64 || !type) {
      setUrl(null)
      return
    }
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
    const objectUrl = URL.createObjectURL(new Blob([bytes], { type: type.mime }))
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [base64, type])
  return url && type ? { url, mime: type.mime, ext: type.ext } : null
}

/** Driver download throws 404 when no image exists; vehicle downloads return null. */
function isNotFound(error: unknown) {
  return isAxiosError(error) && error.response?.status === 404
}

const TILE_W = 150
const TILE_H = 110

function TileFrame({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        width: TILE_W,
        height: TILE_H,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'action.hover',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 0.5,
        color: 'text.secondary',
      }}
    >
      {children}
    </Box>
  )
}

interface DocumentTileProps {
  label: string
  content: string | null | undefined
  isLoading: boolean
  error: unknown
  /** Used for the downloaded file name. */
  fileBaseName: string
}

function DocumentTile({ label, content, isLoading, error, fileBaseName }: DocumentTileProps) {
  const file = useBlobUrl(content)
  const [previewOpen, setPreviewOpen] = useState(false)
  const isImage = file?.mime.startsWith('image/')
  const isPdf = file?.mime === 'application/pdf'
  const fileName = file ? `${fileBaseName}.${file.ext}` : fileBaseName

  let body: ReactNode
  if (isLoading) {
    body = (
      <TileFrame>
        <CircularProgress size={22} />
      </TileFrame>
    )
  } else if (error && !isNotFound(error)) {
    body = (
      <TileFrame>
        <ErrorOutlineIcon color="error" />
        <Typography variant="caption" color="error">
          Failed to load
        </Typography>
      </TileFrame>
    )
  } else if (!file) {
    body = (
      <TileFrame>
        <ImageNotSupportedIcon fontSize="small" />
        <Typography variant="caption">Not uploaded</Typography>
      </TileFrame>
    )
  } else {
    body = (
      <ButtonBase
        onClick={() => setPreviewOpen(true)}
        sx={{ borderRadius: 1 }}
        title={`View ${label}`}
      >
        <TileFrame>
          {isImage ? (
            <Box
              component="img"
              src={file.url}
              alt={label}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <>
              {isPdf ? <PictureAsPdfIcon color="error" /> : <InsertDriveFileIcon />}
              <Typography variant="caption">{isPdf ? 'PDF' : 'File'} — click to view</Typography>
            </>
          )}
        </TileFrame>
      </ButtonBase>
    )
  }

  return (
    <Box sx={{ width: TILE_W }}>
      {body}
      <Typography variant="caption" component="div" sx={{ mt: 0.5, fontWeight: 600 }} noWrap>
        {label}
      </Typography>

      {file && (
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>{label}</DialogTitle>
          <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', bgcolor: 'action.hover' }}>
            {isImage ? (
              <Box
                component="img"
                src={file.url}
                alt={label}
                sx={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
              />
            ) : isPdf ? (
              <Box component="iframe" src={file.url} title={label} sx={{ width: '100%', height: '75vh', border: 0 }} />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                This file type can't be previewed. Download it to view.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button startIcon={<OpenInNewIcon />} href={file.url} target="_blank" rel="noopener">
              Open in new tab
            </Button>
            <Button startIcon={<DownloadIcon />} href={file.url} download={fileName}>
              Download
            </Button>
            <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  )
}

const queryOptions = { retry: false, staleTime: 5 * 60 * 1000 } as const

function DriverDocuments({ driverId }: { driverId: string }) {
  const license = useQuery({
    queryKey: ['driver-doc', driverId, 'license-image'],
    queryFn: () => driverService.getLicenseImage(driverId),
    ...queryOptions,
  })
  return (
    <DocumentTile
      label="Driver license"
      content={license.data}
      isLoading={license.isLoading}
      error={license.error}
      fileBaseName={`driver-${driverId}-license`}
    />
  )
}

function VehicleDocuments({ vehicleId }: { vehicleId: string }) {
  const license = useQuery({
    queryKey: ['vehicle-doc', vehicleId, 'license-image'],
    queryFn: () => vehicleService.getLicenseImage(vehicleId),
    ...queryOptions,
  })
  const plate = useQuery({
    queryKey: ['vehicle-doc', vehicleId, 'plate-image'],
    queryFn: () => vehicleService.getPlateImage(vehicleId),
    ...queryOptions,
  })
  const inspection = useQuery({
    queryKey: ['vehicle-doc', vehicleId, 'inspection-certificate'],
    queryFn: () => vehicleService.getInspectionCertificate(vehicleId),
    ...queryOptions,
  })
  const pictures = useQuery({
    queryKey: ['vehicle-doc', vehicleId, 'pictures'],
    queryFn: () => vehicleService.getPictures(vehicleId),
    ...queryOptions,
  })
  const prefix = `vehicle-${vehicleId}`

  return (
    <>
      <DocumentTile
        label="Vehicle license"
        content={license.data}
        isLoading={license.isLoading}
        error={license.error}
        fileBaseName={`${prefix}-license`}
      />
      <DocumentTile
        label="Plate number"
        content={plate.data}
        isLoading={plate.isLoading}
        error={plate.error}
        fileBaseName={`${prefix}-plate`}
      />
      <DocumentTile
        label="Inspection certificate"
        content={inspection.data}
        isLoading={inspection.isLoading}
        error={inspection.error}
        fileBaseName={`${prefix}-inspection`}
      />
      {pictures.data && pictures.data.length > 0 ? (
        pictures.data.map((p, i) => (
          <DocumentTile
            key={p.documentId}
            label={`Vehicle picture ${i + 1}`}
            content={p.content}
            isLoading={false}
            error={null}
            fileBaseName={`${prefix}-picture-${i + 1}`}
          />
        ))
      ) : (
        <DocumentTile
          label="Vehicle pictures"
          content={null}
          isLoading={pictures.isLoading}
          error={pictures.error}
          fileBaseName={`${prefix}-picture`}
        />
      )}
    </>
  )
}

interface SubjectDocumentsProps {
  subjectType: 'DRIVER' | 'VEHICLE'
  subjectId: string
  /** Section title; pass null to render the tiles without a PropertySection wrapper. */
  title?: string | null
}

/** Uploaded identity documents (driver license, vehicle license / plate / inspection
 *  certificate / pictures) shown as thumbnails with a click-to-enlarge preview. */
export default function SubjectDocuments({ subjectType, subjectId, title = 'Documents' }: SubjectDocumentsProps) {
  const tiles = (
    <Stack direction="row" flexWrap="wrap" sx={{ gap: 2, gridColumn: '1 / -1' }}>
      {subjectType === 'DRIVER' ? (
        <DriverDocuments driverId={subjectId} />
      ) : (
        <VehicleDocuments vehicleId={subjectId} />
      )}
    </Stack>
  )
  if (title === null) return tiles
  return <PropertySection title={title}>{tiles}</PropertySection>
}
